import { useCallback, useEffect, useState } from 'react';
import type { GlColumnDef } from '@/services/accountService/glInvoiceService';
import { fetchLeafGlCategories, type GlCategory } from '@/services/accountService/glCategoryService';
import { listAll as listAllFsCategories, type FsCategory } from '@/services/accountService/fsCategoryService';
import { stripDupSuffix } from '@/pages/components/account/gl-invoice/mode/tabs/bankColumnUtils';

const CJK = /[\u3400-\u9fff]/;

interface ZhLookup {
    glByCode: Map<string, string>;
    glByFs: Map<string, { nameEn: string; nameZh: string }[]>;
    fsByCode: Map<string, string>;
}

function hasCjk(value: string | null | undefined): boolean {
    return CJK.test(value || '');
}

function normName(value: string | null | undefined): string {
    return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function buildLookup(glLeaves: GlCategory[], fsCategories: FsCategory[]): ZhLookup {
    const glByCode = new Map<string, string>();
    const glByFs = new Map<string, { nameEn: string; nameZh: string }[]>();
    for (const cat of glLeaves) {
        const zh = (cat.nameZh || '').trim();
        if (!cat.code || !hasCjk(zh)) continue;
        glByCode.set(cat.code, zh);
        if (!cat.fsCode) continue;
        const list = glByFs.get(cat.fsCode) ?? [];
        list.push({ nameEn: cat.nameEn || '', nameZh: zh });
        glByFs.set(cat.fsCode, list);
    }
    const fsByCode = new Map<string, string>();
    const walk = (nodes: FsCategory[]) => {
        for (const node of nodes) {
            const zh = (node.nameZh || '').trim();
            if (node.code && hasCjk(zh)) fsByCode.set(node.code, zh);
            if (node.children?.length) walk(node.children);
        }
    };
    walk(fsCategories);
    return { glByCode, glByFs, fsByCode };
}

/** 列自身没有中文时：科目代码 → 同一报表分类下唯一英文名 → 报表分类中文。 */
export function fallbackColumnZh(def: Pick<GlColumnDef, 'nameEn' | 'nameZh' | 'glCode' | 'fsCode'>, lookup: ZhLookup | null): string {
    if (!lookup || hasCjk(def.nameZh)) return '';
    const base = stripDupSuffix(def.glCode).replace(/-EXHKD$/, '');
    const byCode = lookup.glByCode.get(def.glCode) || lookup.glByCode.get(base);
    if (byCode) return byCode;

    const en = normName(def.nameEn);
    const pool = lookup.glByFs.get(def.fsCode) ?? [];
    if (en && pool.length) {
        const hits = pool.filter((cat) => {
            const name = normName(cat.nameEn);
            return !!name && (name === en || en.includes(name) || name.includes(en));
        });
        if (hits.length === 1) return hits[0].nameZh;
    }

    for (const code of [def.fsCode, base]) {
        const zh = code ? lookup.fsByCode.get(code) : undefined;
        if (zh) return zh;
    }
    return '';
}

export function useColumnZhLookup() {
    const [lookup, setLookup] = useState<ZhLookup | null>(null);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            fetchLeafGlCategories().catch(() => [] as GlCategory[]),
            listAllFsCategories().catch(() => [] as FsCategory[]),
        ]).then(([glLeaves, fsCategories]) => {
            if (!cancelled) setLookup(buildLookup(glLeaves, fsCategories));
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return useCallback(
        (def: GlColumnDef) => fallbackColumnZh(def, lookup),
        [lookup],
    );
}
